import { BrowserModule } from '@angular/platform-browser';
import { NoopAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, isDevMode } from '@angular/core';



import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HomeComponent } from './home/home.component';
import { FailedComponent } from './failed/failed.component';
import { LoggedOutComponent } from './logged-out/logged-out.component';
import { PwaInstallPromptComponent } from './pwa-install-prompt/pwa-install-prompt.component';

import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { IPublicClientApplication, PublicClientApplication, InteractionType, BrowserCacheLocation, LogLevel } from '@azure/msal-browser';
import { MsalGuard, MsalInterceptor, MsalBroadcastService, MsalInterceptorConfiguration, MsalModule, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalGuardConfiguration, MsalRedirectComponent } from '@azure/msal-angular';

import { environment } from 'src/environments/environment';
import { SharedModule } from './modules/shared/shared.module';
import { AppLayoutModule } from './modules/layout/app.layout.module';
import { TranslateLoader, TranslateService, TranslateStore, TranslateModule } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { MessageService } from 'primeng/api';
import { HttpRequestInterceptor } from './interceptors/http-request.interceptor';
import { TranslationModule } from './infrastructure/di/translation.module';
import { ServiceWorkerModule } from '@angular/service-worker';
import { resolveLoginRedirectUri, resolvePostLogoutRedirectUri } from './utils/msal-auth.util';

export function loggerCallback(logLevel: LogLevel, message: string) {
}

function normalizeAuthorityDomain(domain: string): string {
  return (domain ?? '').replace(/^https?:\/\//i, '').replace(/\/+$/, '');
}

export function MSALInstanceFactory(): IPublicClientApplication {
  const redirectUri = resolveLoginRedirectUri();
  const postLogoutRedirectUri = resolvePostLogoutRedirectUri();
  const authorityDomain = normalizeAuthorityDomain(environment.b2cPolicies.authorityDomain);

  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.b2cPolicies.authorities.signUpSignIn.authority,
      redirectUri,
      postLogoutRedirectUri,
      knownAuthorities: [authorityDomain],

    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    },
    system: {
      allowNativeBroker: false, // Disables WAM Broker
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Verbose,
        piiLoggingEnabled: false
      }
    }
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string> | null>();

  // Público primero (null = sin Bearer). Luego FormEngine autenticado por prefijo
  // más corto: MSAL elige el match más largo, así /public no hereda scopes.
  protectedResourceMap.set(`${environment.host}/api/FormEngine/public`, null);
  protectedResourceMap.set(`${environment.host}/api/FormEngine`, environment.apiConfig.scopes);

  environment.apiConfig.uri.forEach((uri: string) => {
    protectedResourceMap.set(`${environment.host}/${uri}`, environment.apiConfig.scopes);
  });

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.apiConfig.scopes],
      extraQueryParameters: { ui_locales: 'es' }
    },
    loginFailedRoute: '/login-failed'
  };
}

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    FailedComponent,
    LoggedOutComponent,
    PwaInstallPromptComponent
  ],
  imports: [
    AppRoutingModule,
    AppLayoutModule,
    SharedModule,
    BrowserModule,
    NoopAnimationsModule, // Animations cause delay which interfere with E2E tests
    AppRoutingModule,
    MsalModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: (httpClient: HttpClient) => new TranslateHttpLoader(httpClient),
        deps: [HttpClient]
      }
    }),
    TranslationModule,
    HttpClientModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000'
    })
  ],

  providers: [  
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpRequestInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    MessageService
  ],
  // MsalRedirectComponent procesa el code/state tras loginRedirect (selector app-redirect en index.html).
  bootstrap: [AppComponent, MsalRedirectComponent]
})
export class AppModule { }